import {DrawerWrapper} from '@wandb/weave/common/components/DrawerWrapper';
import {type MediaCardType} from '@wandb/weave/common/types/media';
import React from 'react';

import {useRampFlagMediaSettingsDrawerAllTypesUser} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useSupportedMediaDrawerTypes} from '../../PanelMediaBrowser/supportedMediaDrawerTypes';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {
  WORKSPACE_LINE_PLOT_SETTINGS_DESCRIPTION,
  WORKSPACE_MEDIA_SETTINGS_DESCRIPTION,
  WORKSPACE_RUN_DISPLAY_SETTINGS_DESCRIPTION,
} from './constants';
import {useMaybeHasMediaPanel, useMediaKeysAndCardTypes} from './hooks';
import {SettingsMenuItem} from './SettingsMenuItem';
import {type SelectedSettingType} from './types';

export const SettingsMenu = ({
  setSelectedSettingType,
}: {
  setSelectedSettingType: (type: SelectedSettingType | undefined) => void;
}) => {
  const {closeWorkspaceDrawer} = useWorkspaceDrawerContext();
  const {mediaCardTypes} = useMediaKeysAndCardTypes();
  const hasMediaPanel = useMaybeHasMediaPanel();
  const {types: supportedMediaDrawerTypes} = useSupportedMediaDrawerTypes();
  const isAllTypesEnabled =
    useRampFlagMediaSettingsDrawerAllTypesUser() === true;
  // Every type has drawer settings with the gate on, so skip the sampled type
  // lookup, which can miss a key.
  const shouldShowMediaSettings =
    (isAllTypesEnabled && hasMediaPanel) ||
    mediaCardTypes.some((cardType: MediaCardType) =>
      supportedMediaDrawerTypes.includes(cardType)
    );

  return (
    <DrawerWrapper title="Workspace settings" onClose={closeWorkspaceDrawer}>
      <SettingsMenuItem
        dataTest="workspace-layout-menu-item"
        icon="layout-grid"
        color="cactus"
        title="Workspace layout"
        description="Define the overall structure of this workspace, including sectioning logic and panel organization."
        onClick={() => setSelectedSettingType('workspace-layout')}
      />
      <SettingsMenuItem
        dataTest="workspace-semantic-legend-menu-item"
        icon="running-repeat"
        color="green"
        title="Runs"
        description={WORKSPACE_RUN_DISPLAY_SETTINGS_DESCRIPTION}
        onClick={() => setSelectedSettingType('runs')}
      />
      <SettingsMenuItem
        dataTest="workspace-line-plot-menu-item"
        icon="line-plot-alt2"
        color="blue"
        title="Line plots"
        description={WORKSPACE_LINE_PLOT_SETTINGS_DESCRIPTION}
        onClick={() => setSelectedSettingType('line-plot')}
      />
      {shouldShowMediaSettings && (
        <SettingsMenuItem
          dataTest="workspace-media-menu-item"
          icon="photo"
          color="purple"
          title="Media"
          description={WORKSPACE_MEDIA_SETTINGS_DESCRIPTION}
          onClick={() => setSelectedSettingType('media')}
        />
      )}
    </DrawerWrapper>
  );
};
